// @flow
import type { TabsViewProps } from './index';

export const ComponentStateMode = {
  Controlled: 0,
  Uncontrolled: 1
};

export const a11yProps = ({ id, index, label }) => {
  const tabId = `tab-${id || index}`;
  return {
    id: tabId,
    'aria-controls': tabId,
    'data-testid': tabId,
    'aria-label': label
  };
};

export const getModeFromProps = (props: TabsViewProps) => {
  const isControlled = props.selectedTab !== undefined && props.onChangeTabIndex !== undefined;
  const isUncontrolled = props.initialTab !== undefined;

  if (isControlled && isUncontrolled)
    throw new Error(
      'TabsView cannot operate in Controlled and Uncontrolled mode at the same time.'
    );

  return isControlled ? ComponentStateMode.Controlled : ComponentStateMode.Uncontrolled;
};

export const checkForIllegalModeChange = (props: TabsViewProps, mode) => {
  if (process.env.NODE_ENV === 'production') return;

  if (mode === ComponentStateMode.Controlled && props.enableUrlNavigation) {
    throw new Error('URL navigation in `TabsView` could only be used in uncontrolled mode');
  }

  if (mode !== undefined && mode !== getModeFromProps(props)) {
    throw new Error(
      `Switching between controlled and uncontrolled mode is not supported in \`TabsView\`. For controlled mode, you need to pass \`selectedTab\` and \`onChangeTabIndex\` from parent component. 
       For uncontrolled mode, you can pass only \`initialTab\`.`
    );
  }
};

export const buildInitialOrder = ({ organizationStorageKey, tabs }) => {
  try {
    if (!organizationStorageKey) return tabs;

    const saved =
      typeof window !== 'undefined' ? window.localStorage.getItem(organizationStorageKey) : null;
    if (!saved) return tabs;

    const savedIds = JSON.parse(saved);
    if (!Array.isArray(savedIds)) return tabs;

    const map = Object.fromEntries(tabs.map(t => [String(t.id), t]));
    const ordered = savedIds.map(id => map[String(id)]).filter(Boolean);

    const missing = tabs.filter(t => !savedIds.includes(String(t.id)));
    return ordered.concat(missing);
  } catch (e) {
    return tabs;
  }
};
